import React, { useEffect } from 'react';
import * as PropTypes from 'prop-types';
import {
  Form, Radio, Slider, Spin, Button, Switch,
} from 'antd';
import { withRouter } from 'react-router-dom';
import {FormattedMessage, injectIntl} from 'react-intl';
import '../Configure.css';
import { connect } from 'react-redux';
import ConfigHeading from '../components/ConfigHeading';
import {
  selectVideoOptionsPageDataRequest,
} from '../../../../state/ui/configuration/inspection/videoOptionUiReducer';
import {
  requestVideoOptionsPageData,
} from '../../../../state/ui/configuration/inspection/videoOptionUiActions';
import { selectVideoOptions } from '../../../../state/domain/videoOption/videoOptionReducer';
import { selectAppConfigMaxRange } from '../../../../state/domain/appConfig/appConfigReducer';
import { videoOptionsUploadPreference } from './config-util';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { userIsAdmin } from '../company/user-util';
import { configRoutes } from '../../../../utility/route-locations';


const MaxVideosToUploadField = ({ videoOptions, appConfigMaxRange }) => {
  const maxRange = appConfigMaxRange ? appConfigMaxRange.applicationConfigValue : 0;
  const midRange = Math.ceil(maxRange / 2);

  // ticks to depict the starting/middle/end points on the Slider
  const tickMarkings = {
    0: {
      style: {
        color: 'rgba(0, 0, 0, 0.65)', // antd disabled color
      },
      label: <strong>0</strong>,
    },
    [midRange]: {
      style: {
        color: 'rgba(0, 0, 0, 0.65)',
      },
      label: <strong>{midRange}</strong>,
    },
    [maxRange]: {
      style: {
        color: 'rgba(0, 0, 0, 0.65)',
      },
      label: <strong>{maxRange}</strong>,
    },
  };

  return (
    <Form.Item label={<FormattedMessage id="maxNoVideos" />}>
      <div style={{ maxWidth: '380px' }}>
        <Slider
          disabled
          marks={tickMarkings}
          max={Number(appConfigMaxRange ? appConfigMaxRange.applicationConfigValue : 0)}
          defaultValue={videoOptions ? videoOptions.videosAllowed : 0}
        />
      </div>
    </Form.Item>
  );
};

MaxVideosToUploadField.propTypes = {
  videoOptions: PropTypes.shape({
    videosAllowed: PropTypes.number,
    restrictMediaUploadsToWifi: PropTypes.bool,
  }),
  appConfigMaxRange: PropTypes.shape({
    applicationConfigValue: PropTypes.string,
  }),
};

MaxVideosToUploadField.defaultProps = {
  videoOptions: {},
  appConfigMaxRange: null,
};

const UploadPreferenceField = ({ videoOptions }) => (
  <Form.Item label={<FormattedMessage id="uploadPref" />}>
    <Radio.Group buttonStyle="solid" value={videoOptions ? videoOptions.restrictMediaUploadsToWifi : false}>
      <Radio.Button disabled={!videoOptions.restrictMediaUploadsToWifi} value={videoOptionsUploadPreference.WIFI_ONLY}><FormattedMessage id="wiFiOnly" /></Radio.Button>
      <Radio.Button disabled={videoOptions.restrictMediaUploadsToWifi} value={videoOptionsUploadPreference.WIFI_AND_MOBILE}><FormattedMessage id="wiFiAndMobile" /></Radio.Button>
    </Radio.Group>
  </Form.Item>
);

UploadPreferenceField.propTypes = {
  videoOptions: PropTypes.shape({
    videosAllowed: PropTypes.number,
    restrictMediaUploadsToWifi: PropTypes.bool,
  }),
};

UploadPreferenceField.defaultProps = {
  videoOptions: {},
};

const DamageImagesMandatoryFieldRO = injectIntl(({ videoOptions }) => (
    <Form.Item label={<FormattedMessage id="damageImagesMandatory" />}>
        <Switch
            checked={videoOptions.damageImagesMandatory}
            disabled={videoOptions}
        />
    </Form.Item>
));

// MAIN COMPONENT
function VideoOptions({
  videoOptions, loadPageData, appConfigMaxRange, history, sessionUser,
}) {
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  return (
    <ConfigHeading
      titleId="videoOptions"
      descriptionId="videoOptionsDesc"
      actions={userIsAdmin(sessionUser) ? (
        <Button type="primary" icon="edit" loading={((!videoOptions.videosAllowed && videoOptions.videosAllowed > 0) || !appConfigMaxRange)} onClick={() => history.push(configRoutes.configure_edit_video_options)}>
          <FormattedMessage id="edit" />
        </Button>
      ) : null}
    >
      {
        ((!videoOptions.videosAllowed && videoOptions.videosAllowed > 0) || !appConfigMaxRange) ? <Spin />
          : <MaxVideosToUploadField videoOptions={videoOptions} appConfigMaxRange={appConfigMaxRange} />
      }
      <UploadPreferenceField videoOptions={videoOptions} />
      <DamageImagesMandatoryFieldRO videoOptions={videoOptions} />
    </ConfigHeading>
  );
}

VideoOptions.propTypes = {
  loadPageData: PropTypes.func.isRequired,
  videoOptions: PropTypes.shape({
    videosAllowed: PropTypes.number,
    restrictMediaUploadsToWifi: PropTypes.bool,
    damageImagesMandatory: PropTypes.bool,
  }),
  appConfigMaxRange: PropTypes.shape({
    applicationConfigValue: PropTypes.string,
  }),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  history:
    PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
};

VideoOptions.defaultProps = {
  videoOptions: {},
  appConfigMaxRange: null,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectVideoOptionsPageDataRequest(state);
  const appConfigMaxRange = selectAppConfigMaxRange(state);

  return {
    error: pageDataRequest ? pageDataRequest.error : null,
    videoOptions: selectVideoOptions(state),
    appConfigMaxRange,
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestVideoOptionsPageData()),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(VideoOptions));
